import { fail, success } from '@helpers/result-helper.js'
import toast from '@services/toasts/index.js'
import { isHttpStatus, isSuccess } from '@/helpers/http-status-helpers'

export default {
  namespaced: true,
  state: {
    canListLoadingCount: 0,
    loadingCount: 0,
    crudLoadingCount: 0,
    candidateDisplayPictures: [],
    file: null,
    candidateList: [],
    displayPictureLoaders: [],
  },
  getters: {
    moduleName: () => 'candidate',
    isCanListLoading: (state) => state.canListLoadingCount > 0,
    isLoading: (state) => state.loadingCount > 0,
    isLoadingCRUD: (state) => state.crudLoadingCount > 0,
    isDisplayPictureLoading: (state) => (candidateId) =>
      state.displayPictureLoaders.includes(candidateId),
    candidateDisplayPicture: (state) => (candidateId) => {
      const found = state.candidateDisplayPictures.find(
        (pic) => pic.candidateId === candidateId
      )
      if (found) return found.image
      return null
    },
  },
  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    START_CANLIST_LOADING(state) {
      state.canListLoadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    FINISH_CANLIST_LOADING(state) {
      state.canListLoadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    SET_CANDIDATE_LIST(state, data) {
      state.candidateList = data
    },
    START_LOADING_DISPLAY_PICTURE(state, candidateId) {
      state.displayPictureLoaders.push(candidateId)
    },
    FINISH_LOADING_DISPLAY_PICTURE(state, candidateId) {
      const index = state.displayPictureLoaders.indexOf(candidateId)
      if (index !== -1) state.displayPictureLoaders.splice(index, 1)
    },
    SET_DISPLAY_PICTURE(state, data) {
      const existingDisplayPicture = state.candidateDisplayPictures.find(
        (pic) => pic.candidateId === data.candidateId
      )

      if (existingDisplayPicture) {
        existingDisplayPicture.image = data.image
      } else {
        state.candidateDisplayPictures.push(data)
      }
    },
  },
  actions: {
    async loadCandidate({ commit }, id) {
      commit('START_LOADING')
      try {
        const response = await this.$api.candidate.getCandidateDetails(id)

        if (isSuccess(response.status)) {
          return success(response.data)
        }
      } catch {
        toast.error('Candidate data failed to load')
        return fail()
      } finally {
        commit('FINISH_LOADING')
      }
    },

    async getCandidateList({ commit }, clientID) {
      commit('START_CANLIST_LOADING')
      try {
        const response = await this.$api.candidate.getCandidateList(clientID)
        if (isSuccess(response.status)) {
          commit('SET_CANDIDATE_LIST', response.data)
          return success(response.data)
        }
      } catch (ex) {
        if (isHttpStatus(ex.response.status, 'Forbidden')) {
          return fail([], '', 403)
        }

        toast.error(this.$i18n.t('candidateList.candidateListErrorCannotLoad'))
        return fail()
      } finally {
        commit('FINISH_CANLIST_LOADING')
      }
    },

    async getDisplayPic({ commit, getters }, candidateId) {
      // Prevent multiple loads of same image
      if (getters.isDisplayPictureLoading(candidateId)) return success()

      // Use cached image
      const pictureExists = getters.candidateDisplayPicture(candidateId)
      if (pictureExists) return success(pictureExists)

      commit('START_LOADING_DISPLAY_PICTURE', candidateId)
      try {
        const response = await this.$api.candidate.getDisplayPic(candidateId)
        if (isSuccess(response.status)) {
          commit('SET_DISPLAY_PICTURE', {
            candidateId,
            image: response.data.base64ProfileImg,
          })
          return success(response.data.base64ProfileImg)
        }
      } catch {
        return fail()
      } finally {
        commit('FINISH_LOADING_DISPLAY_PICTURE', candidateId)
      }
    },
  },
}
